import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, Pill, EmptyState, Section, Kpi, fmt } from '../components/ui-kit';

interface Tenant {
  id: string;
  name: string;
  tier: string;
  status: 'provisioning' | 'active' | 'suspended';
  users: number;
  revenue_eur: number;
}

export default function Tenants() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<Tenant[] | null>(null);
  const [q, setQ] = React.useState('');

  React.useEffect(() => {
    (async () => {
      try {
        const t = await getToken().catch(() => '');
        const j = await apiGet<{ tenants: Tenant[] }>('/api/v1/admin/tenants', t);
        setItems(j.tenants ?? []);
      } catch {
        setItems([]);
      }
    })();
  }, [getToken]);

  const total = items?.length ?? 0;
  const active = items?.filter((t) => t.status === 'active').length ?? 0;
  const provisioning = items?.filter((t) => t.status === 'provisioning').length ?? 0;
  const suspended = items?.filter((t) => t.status === 'suspended').length ?? 0;
  const arr = items?.reduce((acc, t) => acc + (t.revenue_eur ?? 0), 0) ?? 0;
  const users = items?.reduce((acc, t) => acc + (t.users ?? 0), 0) ?? 0;

  const filtered = (items ?? []).filter(
    (t) =>
      !q ||
      t.name.toLowerCase().includes(q.toLowerCase()) ||
      t.tier.toLowerCase().includes(q.toLowerCase()),
  );
  const filteredRevenue = filtered.reduce((acc, tenant) => acc + (tenant.revenue_eur ?? 0), 0);
  const filteredUsers = filtered.reduce((acc, tenant) => acc + (tenant.users ?? 0), 0);
  const tiers = Array.from(new Set(filtered.map((tenant) => tenant.tier)));

  return (
    <div>
      <PageTitle kicker="Multi-tenant" title="Tenants" />

      <Section title="Portfolio summary" hint="Each tenant is fully isolated by RLS — no cross-tenant data leak.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi accent="green" label="Active" value={fmt(active)} hint={`of ${total}`} />
          <Kpi accent={provisioning > 0 ? 'amber' : undefined} label="Provisioning" value={fmt(provisioning)} />
          <Kpi accent={suspended > 0 ? 'red' : undefined} label="Suspended" value={fmt(suspended)} />
          <Kpi label="Cumulative ARR" value={`${arr.toLocaleString('fr-FR')} €`} hint={`${users} users total`} />
        </div>
      </Section>

      <Section title="Filtered view" hint="Search narrows the tenant list without changing backend scope.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Visible tenants" value={fmt(filtered.length)} hint={q ? 'search applied' : 'full list'} />
          <Kpi accent="blue" label="Visible tiers" value={fmt(tiers.length)} />
          <Kpi label="Visible users" value={fmt(filteredUsers)} />
          <Kpi label="Visible revenue" value={`${fmt(filteredRevenue)} €`} />
        </div>
      </Section>

      <Section title="Ops notes">
        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Provisioning</p>
            <p className="text-sm text-white mt-3">New tenants remain in provisioning until setup is complete.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Active</p>
            <p className="text-sm text-white mt-3">Active tenants are isolated and generating user/revenue telemetry.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Suspended</p>
            <p className="text-sm text-white mt-3">Suspended tenants remain visible for finance and support follow-up.</p>
          </Card>
        </div>
      </Section>

      <Section
        title="Tenants"
        right={
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tenant or tier…"
            className="rounded-md bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none"
            aria-label="Search tenants"
          />
        }
      >
        {items === null && <Card>Loading…</Card>}
        {items && items.length === 0 && <EmptyState title="No tenants yet" />}
        {items && items.length > 0 && (
          <Card>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">
                <tr>
                  <th className="text-left py-3">Tenant</th>
                  <th className="text-left py-3">Tier</th>
                  <th className="text-left py-3">Status</th>
                  <th className="text-right py-3">Users</th>
                  <th className="text-right py-3">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-t border-white/5 hover:bg-white/5 transition">
                    <td className="py-3 text-white">{t.name}</td>
                    <td className="py-3 text-white/60">{t.tier}</td>
                    <td className="py-3">
                      <Pill
                        kind={
                          t.status === 'active'
                            ? 'green'
                            : t.status === 'suspended'
                              ? 'red'
                              : 'amber'
                        }
                      >
                        {t.status}
                      </Pill>
                    </td>
                    <td className="py-3 text-right">{t.users}</td>
                    <td className="py-3 text-right">{t.revenue_eur.toLocaleString('fr-FR')} €</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-white/40 text-xs">
                      No tenant matches “{q}”.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        )}
      </Section>
    </div>
  );
}
